import { computed, Injectable, signal } from '@angular/core';
import { Entity, User } from '@/core/models';
import { LocalStorageService } from '@/core/services/local-storage';

export interface AuthSession {
  accessToken: string;
  refreshToken: string;
  user: User;
  selectedEntity: Entity;
  entities: Entity[];
}

const TOKEN_KEY = 'supervisor-distributor.token';
const REFRESH_TOKEN_KEY = 'supervisor-distributor.refresh-token';
const USER_KEY = 'supervisor-distributor.user';
const CLIENT_ID_KEY = 'supervisor-distributor.client-id';
const SELECTED_ENTITY_KEY = 'supervisor-distributor.selected-entity';
const ENTITIES_KEY = 'supervisor-distributor.entities';

@Injectable({ providedIn: 'root' })
export class AuthSessionService {
  private readonly sessionSignal = signal<AuthSession | null>(this.restoreSession());

  readonly session = this.sessionSignal.asReadonly();
  readonly isAuthenticated = computed(() => !!this.sessionSignal()?.accessToken);
  readonly clientId = computed(() => this.sessionSignal()?.selectedEntity?.id ?? null);

  constructor(private readonly localStorage: LocalStorageService) {}

  saveSession(session: AuthSession): void {
    this.sessionSignal.set(session);
    this.localStorage.setItem(TOKEN_KEY, session.accessToken);
    this.localStorage.setItem(REFRESH_TOKEN_KEY, session.refreshToken);
    this.localStorage.setItem(USER_KEY, session.user);
    this.localStorage.setItem(CLIENT_ID_KEY, session.selectedEntity.id);
    this.localStorage.setItem(SELECTED_ENTITY_KEY, session.selectedEntity);
    this.localStorage.setItem(ENTITIES_KEY, session.entities);
    try {
      localStorage.setItem('market_token', session.accessToken);
    } catch {
      /* ignore */
    }
  }

  getToken(): string | null {
    return this.sessionSignal()?.accessToken ?? this.localStorage.getItem(TOKEN_KEY) ?? null;
  }

  getRefreshToken(): string | null {
    const refreshToken = this.sessionSignal()?.refreshToken || this.localStorage.getItem(REFRESH_TOKEN_KEY) || null;
    return refreshToken || null;
  }

  updateTokens(accessToken: string, refreshToken: string): void {
    const current = this.sessionSignal();
    if (current) {
      this.saveSession({
        ...current,
        accessToken,
        refreshToken,
      });
      return;
    }

    this.localStorage.setItem(TOKEN_KEY, accessToken);
    this.localStorage.setItem(REFRESH_TOKEN_KEY, refreshToken);
    try {
      localStorage.setItem('market_token', accessToken);
    } catch {
      /* ignore */
    }
  }

  clearSession(): void {
    this.sessionSignal.set(null);
    this.localStorage.removeItem(TOKEN_KEY);
    this.localStorage.removeItem(REFRESH_TOKEN_KEY);
    this.localStorage.removeItem(USER_KEY);
    this.localStorage.removeItem(CLIENT_ID_KEY);
    this.localStorage.removeItem(SELECTED_ENTITY_KEY);
    this.localStorage.removeItem(ENTITIES_KEY);
    try {
      localStorage.removeItem('market_token');
    } catch {
      /* ignore */
    }
  }

  private restoreSession(): AuthSession | null {
    const accessToken = this.localStorage.getItem(TOKEN_KEY);
    const refreshToken = this.localStorage.getItem(REFRESH_TOKEN_KEY);
    const user = this.localStorage.getItem(USER_KEY);
    const selectedEntity = this.localStorage.getItem(SELECTED_ENTITY_KEY) as Entity | null;
    const entities = (this.localStorage.getItem(ENTITIES_KEY) as Entity[] | null) ?? [];
    const clientId = this.localStorage.getItem(CLIENT_ID_KEY);

    if (!accessToken || !user || (!selectedEntity && !clientId)) {
      return null;
    }

    return {
      accessToken,
      refreshToken: refreshToken ?? '',
      user,
      selectedEntity: selectedEntity ?? ({ id: clientId } as Entity),
      entities,
    };
  }
}
